import React, { useState } from "react";
import { useNavigate } from "react-router-dom";
import { useDispatch, useSelector } from "react-redux";
import FormContainer from "../../component/shared/FormContainer";
import CustomInput from "../../component/shared/CustomInput";
import Button from "../../component/shared/Button";
import { Api } from "../../network/api";
import { saveToken } from "../../utils/cookies";
import {
  loginStart,
  loginSuccess,
  loginFailure,
} from "../../redux/slice/userSlice";
import { setProfile } from "../../redux/slice/globalSlice";
import { validateForm } from "../../utils/validation";

const LoginForm = () => {
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const { loading, error } = useSelector((state) => state.user);

  const [formData, setFormData] = useState({
    email: "",
    password: "",
  });
  const [errors, setErrors] = useState({});

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({
      ...prev,
      [name]: value,
    }));
    if (errors[name]) {
      setErrors((prev) => ({
        ...prev,
        [name]: "",
      }));
    }
  };

  const validateFormData = () => {
    const validation = validateForm(formData, ["email", "password"]);
    setErrors(validation.errors);
    return validation.isValid;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!validateFormData()) {
      return;
    }

    dispatch(loginStart());

    try {
      const response = await Api.login(formData);
      const data = response.data;
      const userAuth = data?.access_token && data?.is_admin === true;
      if (userAuth) {
        saveToken(response?.data?.access_token);
        dispatch(setProfile(response?.data));
        dispatch(loginSuccess(response?.data));
        navigate("/");
      } else if (data?.is_admin === false) {
        dispatch(
          loginFailure("Your are not authorized to access this page as user")
        );
      }
    } catch (error) {
      console.error("Login error:", error);
      const errorMessage =
        error.response?.data?.detail || "Login failed. Please try again.";
      dispatch(loginFailure(errorMessage));
      setErrors({ general: errorMessage });
    }
  };

  return (
    <FormContainer
      title="Admin Access"
      subtitle="Enter your credentials to access the admin panel"
    >
      <form onSubmit={handleSubmit} className="space-y-6">
        {(errors.general || error) && (
          <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded-xl text-left animate-shake">
            <div className="flex items-center">
              <svg
                className="w-5 h-5 mr-2"
                fill="currentColor"
                viewBox="0 0 20 20"
              >
                <path
                  fillRule="evenodd"
                  d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7 4a1 1 0 11-2 0 1 1 0 012 0zm-1-9a1 1 0 00-1 1v4a1 1 0 102 0V6a1 1 0 00-1-1z"
                  clipRule="evenodd"
                />
              </svg>
              {errors.general || error}
            </div>
          </div>
        )}

        <div className="animate-fadeInUp animation-delay-100">
          <CustomInput
            type="email"
            name="email"
            label="Email"
            // placeholder="Enter your admin email"
            value={formData.email}
            onChange={handleInputChange}
            error={errors.email}
            required
            autoComplete="off"
          />
        </div>

        <div className="animate-fadeInUp animation-delay-200">
          <CustomInput
            type="password"
            name="password"
            label="Password"
            // placeholder="Enter your admin password"
            value={formData.password}
            onChange={handleInputChange}
            error={errors.password}
            required
            autoComplete="current-password"
          />
        </div>

        <div className="flex items-center justify-between animate-fadeInUp animation-delay-300">
          <div className="flex items-center">
            <input
              id="remember-me"
              name="remember-me"
              type="checkbox"
              className="h-4 w-4 text-red-600 focus:ring-red-500 border-gray-300 rounded transition-all duration-200 hover:scale-110"
            />
            <label
              htmlFor="remember-me"
              className="ml-2 block text-sm text-gray-700 hover:text-gray-900 transition-colors duration-200"
            >
              Remember this device
            </label>
          </div>

          <div className="text-sm">
            <button
              type="button"
              onClick={() => navigate("/forgot-password")}
              className="font-medium text-red-600 hover:text-red-500 transition-colors duration-200 hover:underline"
            >
              Forgot password?
            </button>
          </div>
        </div>

        <div className="animate-fadeInUp animation-delay-400">
          <Button
            type="submit"
            loading={loading}
            disabled={loading}
            className="w-full hover-lift"
            size="large"
          >
            {loading ? "Authenticating..." : "Access Admin Panel"}
          </Button>
        </div>
      </form>
    </FormContainer>
  );
};

export default LoginForm;
